import { AutobuyerInputFunctions } from "@/components/tabs/autobuyers/AutobuyerInput";

export const automatorTemplates = {
  /**
    * List of possible data types to dynamically generate in script templates, assumed to be only string or boolean
    * {
    *  @property {String} name              String to be used as a key for entries in this object
    *  @property {String[]} boolDisplay     Strings to be displayed for true/false states for boolean inputs. If
    *   undefined, assumed to be a non-boolean input
    *  @property {Function} isValidString   A function used to test if an input string is formatted properly or not
    *  @property {Function} map             A function to be used to map the inputs to their actual values
    *   which are stored in the param object. If undefined, assumed to be no mapping
    * }
    */
  paramTypes: [
    {
      name: "tree",
      isValidString: str => {
        const validImport = TimeStudyTree.isValidImportString(str);
        const preset = str.match(/^(NAME (.{1,4})|ID (\d))$/u);
        const validPreset = preset ? (
          player.timestudy.presets.some(p => p.name === preset[2]) ||
          (Number(preset[3]) > 0 && Number(preset[3]) < 7)
        ) : false;
        return validImport || validPreset;
      },
    },
    {
      name: "integer",
      isValidString: str => AutobuyerInputFunctions.int.tryParse(str),
      map: x => Math.round(parseInt(x, 10)),
    },
    {
      name: "decimal",
      isValidString: str => AutobuyerInputFunctions.decimal.tryParse(str),
      map: x => AutobuyerInputFunctions.decimal.tryParse(x),
    },
    {
      name: "boolean",
      boolDisplay: [true, false],
    },
    {
      name: "nowait",
      boolDisplay: ["Продолжать дальше", "Продолжать покупать Исследования"],
    },
    {
      name: "mode",
      boolDisplay: ["в X раз больше наибольших", "Секунд с предыдущего"],
      map: x => (x ? "mult" : "time"),
    },
  ],
  /**
    * List automator script templates, primarily used here for formatting the player UI prompts appropriately
    * so that all of the required fields show up in the proper input formats. Actual script formatting requires
    * additionally writing a method to be called in the constructor of the ScriptTemplate class
    * {
    *  @property {String} name          Name of script template, also used as a key within the constructor for
    *   ScriptTemplate objects
    *  @property {String} description   Text description of what the template does when used in the automator
    *  @property {Object[]} inputs      Fields of the param object which need to be filled for the template to
    *   have all the information it needs. Contains the name of the field, the type (drawn from paramTypes above),
    *   and a prompt to be shown in the UI end
    *  @property {Function} warnings    Function which checks the current game state and potentially provides
    *   warnings based on some possibly common cases which may lead to undesired behavior
    * }
    */
  scripts: [
    {
      name: "Climb EP",
      description: `Этот скрипт делает повторяющиеся Вечности, позволяя перепокупать Древо Исследований каждую
        Вечность. Настройки АвтоПокупщиков должны быть предоставлены для АвтоПокупщиков Бесконечности и Вечности. Скрипт
        будет повторяться до тех пор, пока финальное значение Очков Вечности не будет достигнуто.`,
      inputs: [
        { name: "treeStudies", type: "tree", prompt: "Или напрямую введите ваши временные исследования" },
        { name: "treeNowait", type: "nowait", prompt: "Поведение отсутствующих исследований" },
        { name: "finalEP", type: "decimal", prompt: "Целевые ОВ" },
        { name: "autoInfMode", type: "mode", prompt: "Режим АвтоПокупщика Бесконечности" },
        { name: "autoInfValue", type: "decimal", prompt: "Задержка АвтоПокупщика Бесконечности" },
        { name: "autoEterMode", type: "mode", prompt: "Режим АвтоПокупщика Вечности" },
        { name: "autoEterValue", type: "decimal", prompt: "Задержка АвтоПокупщика Вечности" },
      ],
      warnings: () => {
        const list = [];
        if (!RealityUpgrade(10).isBought) {
          list.push(`Скрипт не сможет корректно установить режимы АвтоПокупщиков без как минимум ${formatInt(100)}
            Вечностей. Подумайте о покупке Улучшения Реальности "${RealityUpgrade(10).name}" до использования этого
            в начале Реальности.`);
        }
        // Telemechanical Process (TD/5xEP autobuyers)
        if (!RealityUpgrade(13).isBought) {
          list.push(`Этот шаблон может плохо работать без Улучшения Реальности "${RealityUpgrade(13).name}"`);
        }
        if (!Perk.ttBuySingle.isBought) {
          list.push(`Этот шаблон может плохо работать без Перка "${Perk.ttBuySingle.label}", только если вы не можете
            генерировать Временные Теоремы без их покупки`);
        }
        return list;
      },
    },
    {
      name: "Grind Eternities",
      description: `Этот скрипт повторяет быстрые Вечности после покупки определённого Древа Временных Исследований.
        Авто-Бесконечность будет установлена на "В раз больше" с определённым количеством хрустов и Авто-Вечность
        будет срабатывать как можно быстрее. Скрипт будет повторяться пока финальное значение Вечности не будет
        достигнуто.`,
      inputs: [
        { name: "treeStudies", type: "tree", prompt: "Или напрямую введите ваши временные исследования" },
        { name: "treeNowait", type: "nowait", prompt: "Поведение отсутствующих исследований" },
        { name: "crunchesPerEternity", type: "integer", prompt: "Хрустов за Вечность" },
        { name: "eternities", type: "decimal", prompt: "Целевое Количество Вечностей" },
      ],
      warnings: () => {
        const list = [];
        // Eternal flow (eternity generation)
        if (RealityUpgrade(14).isBought) {
          list.push(`Вам скорее всего не нужно использовать это из-за Улучшения Реальности "${
            RealityUpgrade(14).name}"`);
        }
        return list;
      },
    },
    {
      name: "Grind Infinities",
      description: `Этот скрипт покупает указанное Древо Временных Исследований и затем настраивает АвтоПокупщики
        на заработок Бесконечностей. Он будет повторяться пока финальное количество Бесконечностей не будет достигнуто;
        количество может быть для Запасённых Бесконечностей, в таком случае он получит все Бесконечности перед тем как
        совершить Вечность.`,
      inputs: [
        { name: "treeStudies", type: "tree", prompt: "Или напрямую введите ваши временные исследования" },
        { name: "treeNowait", type: "nowait", prompt: "Поведение Отсутствующих Исследований" },
        { name: "infinities", type: "decimal", prompt: "Целевое Количество Бесконечностей" },
        { name: "isBanked", type: "boolean", prompt: "Использовать Запасённые в Качестве Цели?" },
      ],
      warnings: () => {
        const list = [];
        if (!Perk.achievementGroup5.isBought) {
          list.push(`Вы не начнёте эту Реальность с Достижением "${Achievement(131).name}" - накопление
            Бесконечностей может быть менее полезным чем ожидается, так как они не могут быть Запасены`);
        }
        // Boundless flow (infinity generation)
        if (RealityUpgrade(11).isBought) {
          list.push(`Вам скорее всего не следует использовать это из-за Улучшения Реальности "${
            RealityUpgrade(11).name}"`);
        }
        return list;
      },
    },
    {
      name: "Complete Eternity Challenge",
      description: `Этот скрипт покупает определённое Древо Временных Исследований и затем разблокирует определённый
        Вечный Челлендж. Затем он установит АвтоПокупщик Бесконечности на указанные настройки и войдет в Вечный
        Челлендж. Наконец, он будет ждать желаемого количества прохождений, прежде чем завершить Вечность для
        прохождения данного Челленджа.`,
      inputs: [
        { name: "treeStudies", type: "tree", prompt: "Или напрямую введите ваши временные исследования" },
        { name: "treeNowait", type: "nowait", prompt: "Поведение Отсутствующих Исследований" },
        { name: "ec", type: "integer", prompt: "Айди Вечного Челленджа" },
        { name: "completions", type: "integer", prompt: "Целевое Количество Прохождений" },
        { name: "autoInfMode", type: "mode", prompt: "Режим АвтоПокупщика Бесконечности" },
        { name: "autoInfValue", type: "decimal", prompt: "Задержка АвтоПокупщика Бесконечности" },
      ],
      warnings: () => {
        const list = [];
        if (!Perk.studyECRequirement.isBought) {
          list.push(`Вечные Челленджи могут не быть разбликированны из-за вторичных требований, подумайте о
            разблокировке Перка "${Perk.studyECRequirement.label}" перед использованием этого шаблона`);
        }
        if (!Perk.studyECBulk.isBought) {
          list.push(`Используя этот шаблон без объемного прохождения Вечных Челленджей может привести к длинному
            скрипту, который будет медленный и сложный для изменений. Если вы используете этот шаблон, подумайте о 
            возвращении для упрощения ваших скриптов после разблокировки Перка "${Perk.studyECBulk.label}"`);
        }
        return list;
      },
    },
    {
      name: "Unlock Dilation",
      description: `Этот скрипт повторяет Вечности, пытаясь заново купить Древо Временных Исследований каждую Вечность.
        Настройки должны быть указаны для АвтоПокупщика Вечности; ваш АвтоПокупщик Бесконечности будет отключен.
        Скрипт входит в петлю, до тех пор поку у вас нет достаточного количества Временных Теорем для разблокировки
        Растяжения. Затем скрипт разблокирует Растяжение и выйдет из петли.`,
      inputs: [
        { name: "treeStudies", type: "tree", prompt: "Или напрямую введите ваши временные исследования" },
        { name: "treeNowait", type: "nowait", prompt: "Поведение Отсутствующих Исследований" },
        { name: "finalEP", type: "decimal", prompt: "Целевые ОВ" },
        { name: "autoEterMode", type: "mode", prompt: "Режим АвтоПокупщика Вечности" },
        { name: "autoEterValue", type: "decimal", prompt: "Задержка АвтоПокупщика Вечности" },
      ],
      warnings: () => {
        const list = [];
        // Telemechanical Process (TD/5xEP autobuyers)
        if (!RealityUpgrade(13).isBought) {
          list.push(`Этот шаблон может плохо работать без Улучшения Реальности "${RealityUpgrade(13).name}"`);
        }
        if (!Perk.ttBuySingle.isBought) {
          list.push(`Этот шаблон может плохо работать без Перка "${Perk.ttBuySingle.label}", в случае если вы не
            можете генерировать Временные Теоремы без него`);
        }
        return list;
      },
    },
  ]
};
